import { Flex, Text, useDisclosure, Button, Tooltip } from "@chakra-ui/react";
import { Layout } from "@/components/Layout/Layout";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { Pagination } from "@/components/Pagination/Pagination";
import { createColumnHelper } from "@tanstack/react-table";
import { useState, useMemo, useEffect } from "react";
import { formatDate } from "@/helpers/formatDate";
import { Lead, LeadsFilter } from "@/model/lead";
import { LeadsFilters } from "@/api/leads/types";
import { LeadsPopupFilter } from "./components/LeadsPopupFilter";
import { defaultLeadsFilterFields } from "./_helpers";
import { useSessionStorageFilters } from "@/hooks/useSessionStorageFilters";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Recruiter } from "@/model/recruiters";
import api from "@/api";
import { useSorting } from "@/hooks/useSorting";
import { toUnix } from "@/utils/date";
import { getOffset } from "@/utils/pagination";
import { RootState, useAppSelector } from "@/store";
import { updateRouteQuery } from "@/utils/route";

export const LeadsPage = () => {
  const navigate = useNavigate();
  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();
  const [activeTab, setActiveTab] = useState<"all" | "unsorted">("all");
  const [leads, setLeads] = useState<readonly Lead[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 20;
  const { sortColumn, sortDirection, handleSort } = useSorting("created_at");

  // Инициализация activeTab из URL
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") as "all" | "unsorted" | null;
    if (tabFromUrl === "all" || tabFromUrl === "unsorted") {
      setActiveTab(tabFromUrl);
    }
  }, []);

  const { formik, handleSearch, handleApplyFilters, handleResetFilters } = useSessionStorageFilters<LeadsFilter>({
    storageKey: "leadsPageFilters",
    defaultValues: defaultLeadsFilterFields,
  });

  const { guides } = useAppSelector((state: RootState) => state.guides);
  const [recruiters, setRecruiters] = useState<readonly Recruiter[]>([]);

  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const recruitersRes = await api.recruiters.recruiters({});
        setRecruiters(recruitersRes?.data || []);
      } catch {
        setRecruiters([]);
      }
    };
    fetchRecruiters();
  }, []);

  useEffect(() => {
    const fetchLeads = async () => {
      setIsLoading(true);
      try {
        const offset = getOffset(page, limit);
        const { created_at_from, created_at_to, status_id, source_id, responsible_user_id, search } = formik.values;

        const computedFilters: LeadsFilters = {
          sort_column: sortColumn || undefined,
          sort_direction: sortDirection || undefined,
          limit,
          offset,
        };

        const created_at_from_unix = toUnix(created_at_from);
        if (created_at_from_unix) {
          computedFilters.created_at_from = Number(created_at_from_unix);
        }

        const created_at_to_unix = toUnix(created_at_to);
        if (created_at_to_unix) {
          computedFilters.created_at_to = Number(created_at_to_unix);
        }

        if (search) {
          computedFilters.search = search;
        }

        if (status_id) {
          computedFilters.status_id = status_id;
        }
        if (source_id) {
          computedFilters.source_id = Number(source_id);
        }
        if (responsible_user_id) {
          computedFilters.responsible_user_id = Number(responsible_user_id);
        }

        if (activeTab === "unsorted") {
          const leadStatusMap = guides?.LeadStatus as Record<string, string> | undefined;
          if (leadStatusMap) {
            const newStatusId = Object.entries(leadStatusMap).find(([_, label]) => label === "Новый")?.[0];
            if (newStatusId) {
              computedFilters.status_id = newStatusId;
            }
          }
        }

        const response = await api.leads.leads({
          params: {
            expand: ["status", "responsible_user", "source", "contact", "created_at"],
            filters: computedFilters,
          },
        });

        setLeads(response?.data?.items || []);
        setTotalItems(response?.data?.count || 0);
      } catch (error) {
        setLeads([]);
        setTotalItems(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeads();
  }, [page, limit, formik.values, sortColumn, sortDirection, activeTab, guides]);

  const columnHelper = createColumnHelper<Lead>();

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: "Название",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="200px" fontSize={14} fontWeight={600} color="#3b515e">
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 200,
      minSize: 150,
    }),
    columnHelper.accessor("responsible_user.name", {
      id: "responsible_user.name",
      header: "Ответственный",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="150px" fontSize={14}>
            {info.getValue() || "Не назначен"}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 150,
      minSize: 120,
    }),
    columnHelper.accessor("status.name", {
      id: "status.name",
      header: "Статус",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 100,
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Дата создания",
      cell: (info) => (
        <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {formatDate(info.getValue() || "")}
          </Text>
        </Tooltip>
      ),
      enableSorting: true,
      size: 120,
      minSize: 100,
    }),
  ];

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (formik.values.status_id) count += 1;
    if (formik.values.source_id) count += 1;
    if (formik.values.responsible_user_id) count += 1;
    if (formik.values.created_at_from) count += 1;
    if (formik.values.created_at_to) count += 1;
    return count;
  }, [
    formik.values.status_id,
    formik.values.source_id,
    formik.values.responsible_user_id,
    formik.values.created_at_from,
    formik.values.created_at_to,
  ]);

  // Синхронизация фильтров с URL
  useEffect(() => {
    const values = formik.values;
    updateRouteQuery(
      {
        tab: activeTab,
        search: values.search || "",
        status_id: values.status_id || "",
        source_id: values.source_id || "",
        responsible_user_id: values.responsible_user_id || "",
        created_at_from: values.created_at_from || "",
        created_at_to: values.created_at_to || "",
        sort_column: sortColumn || "",
        sort_direction: sortDirection || "",
        page: String(page),
        limit: String(limit),
      },
      { replace: true },
    );
  }, [formik.values, activeTab, sortColumn, sortDirection, page, limit]);

  const handleCreateLead = () => {
    navigate("/leads/create");
  };

  const handlePageChange = (offset: number, limit: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(offset / limit + 1));
    params.set("limit", String(limit));
    navigate(`?${params.toString()}`);
  };

  const handleTabChange = (tab: "all" | "unsorted") => {
    setActiveTab(tab);
    updateRouteQuery({ tab, page: "1" }, { replace: false });
  };

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Text fontSize={27} fontWeight={500}>
          Лиды
        </Text>

        {/* Контейнер для фильтров */}
        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по лидам"
          searchValue={formik.values.search || ""}
          isShowBtnPopupFilters
          selectedFiltersCount={appliedFiltersCount}
          onClickBtnPopupFilters={onFiltersOpen}
          isOpenPopupFilters={isFiltersOpen}
          onClosePopupFilters={onFiltersClose}
          popupFilters={
            <LeadsPopupFilter
              values={formik.values}
              onApply={(v) => {
                handleApplyFilters(v);
                onFiltersClose();
              }}
              onReset={() => {
                handleResetFilters();
                onFiltersClose();
              }}
              recruiters={recruiters}
            />
          }
          isShowCreateButton
          onClickCreate={handleCreateLead}
          isLoading={isLoading}
        />

        {/* Табы */}
        <Flex w="100%" justifyContent="space-between" alignItems="center">
          <Flex border="1px solid #E5E7EB" borderRadius="10px" p="6px" gap="8px" bg="#F8FAFC" w="100%">
            {[
              { id: "all" as const, label: "Все" },
              { id: "unsorted" as const, label: "Нераспределенные" },
            ].map((tab) => (
              <Button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                bg={activeTab === tab.id ? "#3B82F6" : "transparent"}
                color={activeTab === tab.id ? "white" : "#5C657E"}
                _hover={{ bg: activeTab === tab.id ? "#3B82F6" : "#EEF2F7" }}
                borderRadius="8px"
                px="16px"
                py="8px"
                w="50%"
              >
                {tab.label}
              </Button>
            ))}
          </Flex>
        </Flex>

        {/* Таблица */}
        <BaseTable
          list={leads}
          columns={columns}
          sortHandler={({ id, direction }) => handleSort({ id, direction: direction ?? null })}
          sortState={{
            column: sortColumn || "",
            direction: sortDirection,
          }}
          isLoading={isLoading}
          clickHandler={(id) => navigate(`/leads/${id}`)}
        />

        {/* Пагинация */}
        <Pagination totalItems={totalItems} onPageChange={handlePageChange} isLoading={isLoading} />
      </Flex>
    </Layout>
  );
};
